import { Flame, Moon, Brain, CloudRain, BatteryLow, Scale, CalendarClock, Sparkles } from "lucide-react";
import { Reveal } from "./Reveal";

const PAIN_POINTS = [
  {
    icon: Flame,
    title: "Hot flashes and night sweats",
    feel: "A sudden wave of heat out of nowhere, or waking up drenched with the sheets soaked through.",
    why: "Shifting estrogen can affect the brain's internal thermostat, triggering sudden changes in how your body regulates heat.",
  },
  {
    icon: Moon,
    title: "Sleep that falls apart",
    feel: "Wide awake at 3 a.m. for no reason, or sleeping all night and still waking exhausted.",
    why: "Declining progesterone and night sweats both disrupt sleep, and broken sleep makes nearly every other symptom feel worse.",
  },
  {
    icon: Brain,
    title: "Brain fog",
    feel: "Losing words mid-sentence, rereading the same email three times, walking into rooms and forgetting why.",
    why: "Fluctuating estrogen influences memory and concentration. This is a widely reported perimenopause experience, not a personal failing.",
  },
  {
    icon: CloudRain,
    title: "Mood changes and new anxiety",
    feel: "A shorter fuse than you've ever had, tearfulness, or anxiety that showed up uninvited in your 40s.",
    why: "Hormone shifts influence the brain chemistry that steadies mood, so emotional changes can appear even when life hasn't changed.",
  },
  {
    icon: BatteryLow,
    title: "Fatigue that rest doesn't fix",
    feel: "Running on empty by mid-afternoon, relying on caffeine to get through what used to be an ordinary day.",
    why: "Fragmented sleep, hormonal changes, and other factors can compound. Untangling which is which takes time and a careful history.",
  },
  {
    icon: Scale,
    title: "Weight and body-composition changes",
    feel: "Weight settling around your middle even though your eating and exercise haven't changed.",
    why: "As estrogen falls, metabolism and muscle mass shift. That's why your first visit includes an InBody body-composition assessment.",
  },
  {
    icon: CalendarClock,
    title: "Cycles that stop making sense",
    feel: "Periods arriving closer together, heavier, lighter, or skipping months entirely.",
    why: "Ovulation becomes irregular during the transition, so cycle changes are often one of the earliest signals.",
  },
  {
    icon: Sparkles,
    title: "The surprise symptoms",
    feel: "Heart palpitations, new joint or muscle aches, dry skin, hair loss, more frequent UTIs, or changes in intimacy and libido.",
    why: "Estrogen receptors exist throughout the body, so the transition can show up in places that seem completely unrelated to hormones.",
  },
];

export const PerimenopauseDeep = ({ onStartGuide, onDownloadGuide }) => (
  <section id="perimenopause" data-testid="perimenopause-section" className="scroll-mt-20 py-16 sm:py-24 bg-[var(--brand-navy-deep)] text-white">
    <div className="max-w-6xl mx-auto px-4 sm:px-6">
      <Reveal>
        <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-[var(--brand-gold)] mb-3">
          Understanding perimenopause
        </p>
        <h2 className="font-serif-brand text-3xl sm:text-4xl font-medium leading-tight">
          Perimenopause can start years before your last period.
        </h2>
        <div className="mt-6 grid lg:grid-cols-2 gap-6 lg:gap-12 text-lg leading-relaxed text-white/85">
          <p>
            Perimenopause is the transition leading up to menopause, when your ovaries slow down and
            estrogen and progesterone stop following their usual rhythm. It typically begins 3 to 10
            years before menopause, and symptoms can start as early as 35. Your periods may still be
            regular when the first symptoms appear, which is one reason so many women are told
            &ldquo;you&rsquo;re too young for this.&rdquo;
          </p>
          <p>
            You&rsquo;ve probably blamed your stress, your kids, your job, or your schedule. But
            what you&rsquo;re feeling may not be a personality flaw. It may be physiology. And
            because hormone levels swing from day to day during this transition, a single blood test
            can look normal on paper while your symptoms are very real.
          </p>
        </div>
        <div className="mt-10 grid sm:grid-cols-3 gap-px bg-[var(--brand-gold)]/40 border border-[var(--brand-gold)]/40">
          {[
            {
              h: "Progesterone declines first",
              b: "Often the earliest shift, and a common reason sleep and mood are the first things to suffer.",
            },
            {
              h: "Estrogen swings wildly",
              b: "Unpredictable surges and crashes drive hot flashes, migraines, and new anxiety.",
            },
            {
              h: "Testosterone slowly fades",
              b: "A gradual decline that can chip away at motivation, muscle, and libido, though not for everyone.",
            },
          ].map((x) => (
            <div key={x.h} className="bg-[var(--brand-navy-deep)] p-5 sm:p-6">
              <p className="font-serif-brand text-lg font-medium text-[var(--brand-gold)]">{x.h}</p>
              <p className="mt-2 text-sm leading-relaxed text-white/80">{x.b}</p>
            </div>
          ))}
        </div>
      </Reveal>
      <div className="mt-12 grid sm:grid-cols-2 gap-px bg-white/15 border border-white/15">
        {PAIN_POINTS.map((p, i) => (
          <Reveal key={p.title} delay={(i % 2) * 0.08} className="bg-[var(--brand-navy-deep)]">
            <div data-testid={`pain-point-${i}`} className="p-6 sm:p-7 h-full">
              <p.icon className="h-6 w-6 text-[var(--brand-gold)]" aria-hidden="true" />
              <h3 className="mt-3 font-serif-brand text-xl font-medium">{p.title}</h3>
              <p className="mt-2 text-base leading-relaxed text-white/85">{p.feel}</p>
              <p className="mt-3 text-sm leading-relaxed text-[var(--brand-gold)]/90">
                <span className="font-semibold uppercase tracking-wide text-xs">Why it can happen: </span>
                {p.why}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1}>
        <div className="mt-10 border border-[var(--brand-gold)]/50 bg-white/5 p-6 sm:p-8 grid gap-8 lg:grid-cols-[auto_1fr_auto] lg:items-center">
          <div className="guide-book mx-auto lg:mx-0 w-36 sm:w-44">
            <div className="guide-book-inner">
              <img src="/images/guide-cover.webp" alt="Cover of the All-In Precision Wellness Perimenopause Guide" />
            </div>
          </div>
          <div>
            <h3 className="font-serif-brand text-2xl font-medium">
              You&rsquo;re not broken. You don&rsquo;t have to white-knuckle through this.
            </h3>
            <p className="mt-2 text-base sm:text-lg leading-relaxed text-white/85 max-w-2xl">
              Every symptom on this list can have more than one cause, and not every symptom is
              hormonal. That is exactly why your first visit is a full hour with a physician who
              takes the time to sort out what is going on with you. Start with our free
              Perimenopause Guide, then take the next step when you&rsquo;re ready.
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <button
              data-testid="perimenopause-cta-button"
              onClick={onStartGuide}
              className="inline-flex items-center justify-center bg-[var(--brand-gold)] text-[var(--brand-navy-deep)] text-lg font-semibold px-8 py-3.5 hover:bg-white transition-colors"
            >
              Find My Next Step
            </button>
            <button
              data-testid="perimenopause-guide-button"
              onClick={onDownloadGuide}
              className="inline-flex items-center justify-center border-2 border-[var(--brand-gold)] text-[var(--brand-gold)] text-lg font-semibold px-6 py-3 hover:bg-[var(--brand-gold)] hover:text-[var(--brand-navy-deep)] transition-colors"
            >
              Download the Free Guide
            </button>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
